import React from 'react';
import { useNavigate } from 'react-router';
import Card from '@mui/material/Card';
import { useTheme } from '@mui/material/styles';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import IconButton from '@mui/material/IconButton';
import { Tooltip } from '@mui/material';
import useSuspenseFetch from '../hooks/useSuspenseFetch';

const AuthAndAdminStatus = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { data: user } = useSuspenseFetch('user');
    const { data: accountInfo } = useSuspenseFetch('account');
    const domain = accountInfo.sendingDomain ? accountInfo.sendingDomain.substring(accountInfo.sendingDomain.indexOf('.') + 1) : '';

    const isAuthenticated =
        accountInfo.mxCheck &&
        accountInfo.dkim &&
        accountInfo.dmarc &&
        accountInfo.spf;

    const hasManageUsersPermission = (user) =>
        user.permissions.some((permission) => permission.name === 'Manage Users');

    const usersWithPermission = accountInfo.users
        .filter((u) => hasManageUsersPermission(u))
        .sort((a, b) => {
            const isLoggedInUser = (u) => u.emailAddress === user.emailAddress;

            if (isLoggedInUser(a)) return -1;
            if (isLoggedInUser(b)) return 1;

            return 0;
        });

    const isSingleUser = accountInfo.users.length === 1;

    return (
        <Grid container spacing={1}>
            <Grid className="email-auth" size={{ xs: 12, sm: isSingleUser ? 12 : 6 }}>
                <Card
                    elevation={4}
                    sx={{
                        minHeight: 150,
                        maxHeight: 150,
                        borderRadius: 3,
                        px: 1,
                        borderTop: isAuthenticated ? 'none' : `5px solid ${theme.palette.error.main}`,
                        '&:hover': {
                            elevation: 16,
                            boxShadow: 10,
                            '.action-icon': { opacity: 1 }
                        }
                    }}
                >
                    <CardHeader
                        sx={{ pb: 0 }}
                        title={(
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{
                                    fontWeight: 'bold',
                                    color: 'primary.main'
                                }}
                            >
                                Email Authentication
                            </Typography>
                        )}
                        action={(
                            <Tooltip title="Configuration" arrow>
                                <IconButton
                                    className="action-icon"
                                    sx={{
                                        opacity: 0,
                                        transition: 'opacity 0.3s'
                                    }}
                                    onClick={() => {
                                        navigate('/account/configuration');
                                    }}
                                >
                                    <ArrowForwardIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    />
                    <CardContent sx={{ pt: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            {isAuthenticated ? (
                                <CheckCircleOutlineIcon color="success" fontSize="medium" sx={{ mr: 1 }} />
                            ) : (
                                <ErrorOutlineIcon color="error" fontSize="medium" sx={{ mr: 1 }} />
                            )}
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: 'medium', overflowWrap: 'anywhere' }}
                            >
                                {domain}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            {
                !isSingleUser && (
                    <Grid className="admin-users" size={{ xs: 12, sm: 6 }}>
                        <Card
                            elevation={4}
                            sx={{
                                minHeight: 150,
                                maxHeight: 150,
                                borderRadius: 3,
                                px: 1,
                                '&:hover': {
                                    elevation: 16,
                                    boxShadow: 10,
                                    '.action-icon': { opacity: 1 }
                                }
                            }}
                        >
                            <CardHeader
                                sx={{ pb: 0 }}
                                title={(
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'primary.main'
                                        }}
                                    >
                                        Admin Users
                                    </Typography>
                                )}
                                action={(
                                    <Tooltip title="My Account/Users" arrow>
                                        <IconButton
                                            className="action-icon"
                                            sx={{
                                                opacity: 0,
                                                transition: 'opacity 0.3s'
                                            }}
                                            onClick={() => {
                                                navigate('/account', { state: { tab: 'userLimit' } });
                                            }}
                                        >
                                            <ArrowForwardIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            />
                            <CardContent sx={{ pt: 0, px: 1 }}>
                                <Box
                                    sx={{
                                        maxHeight: 80,
                                        overflowY: 'auto',
                                        '&::-webkit-scrollbar': {
                                            width: '6px'
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                            borderRadius: '3px'
                                        }
                                    }}
                                >
                                    {usersWithPermission.map((userDetail) => (
                                        <ListItem key={userDetail.id} sx={{ p: 0 }}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    sx={{
                                                        width: 35,
                                                        height: 35
                                                    }}
                                                    alt={userDetail.name}
                                                    src={userDetail.avatarURL}
                                                />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={(
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            whiteSpace: 'normal',
                                                            wordWrap: 'break-word'
                                                        }}
                                                    >
                                                        {userDetail.name}
                                                        {user.emailAddress === userDetail.emailAddress && (
                                                            <Chip
                                                                label="You"
                                                                color="primary"
                                                                size="small"
                                                                sx={{ ml: 1 }}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                                secondary={userDetail.emailAddress}
                                                sx={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word'
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )
            }
        </Grid>
    );
};

export default AuthAndAdminStatus;